import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {StaticImageData} from 'next/image';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import parse from 'html-react-parser';
import {colors, borderRadius, fontSize, fontWeight, gradients, lineHeightRatio, shadows, spacing, transition} from '@/styles/tokens';
import {ICON_TILE_ICON_SIZE, iconTileImageStyle, iconTileSx} from '@/styles/iconTile';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface TitlePart {
    label: string;
    redirect?: string;
}

interface Service {
    title?: string;
    titleParts?: TitlePart[];
    description: string;
    /** Omit to render the card without an icon tile. */
    icon?: string | StaticImageData | React.ReactNode;
    redirect?: string;
    badges?: string[];
}

interface KeyServicesProps {
    heading: string;
    subheading: string;
    /** Omit to render the sticky column without the supporting image. */
    imageSrc?: string | StaticImageData;
    services: Service[];
}

// Icons are flat monochrome images (svg + webp), so they are recoloured with a filter chain rather than
// a fill/mask. `brightness(0) saturate(100%)` flattens any source to black, and the rest tints that black
// to colors.primary.deep (#1F3F7A) exactly — keep in sync if the token changes.
const iconBlueFilter =
    'brightness(0) saturate(100%) invert(43.5%) sepia(99.3%) saturate(486.3%) hue-rotate(185deg) brightness(48%) contrast(106.2%)';

const imageWrapperSx = {
    position: 'relative',
    width: '100%',
    height: {xs: 300, sm: 400, md: 450},
    borderRadius: borderRadius.xl,
    overflow: 'hidden',
    boxShadow: shadows.awardCardElevated,
    mt: spacing[32]
} as const;

const rightColumnSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 3
} as const;

const serviceCardSx = {
    display: 'flex',
    flexDirection: {xs: 'column', sm: 'row'},
    alignItems: 'flex-start',
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.lg,
    p: {xs: 1.5, md: 2},
    boxShadow: shadows.lg,
    backgroundImage: 'none',
    transition: `background-color ${transition.base}, box-shadow ${transition.base}, transform ${transition.base}`,
    '&:hover': {
        backgroundImage: gradients.navyCardDeep,
        transform: 'scale(1.02)',
        boxShadow: shadows.lg,
        '& .font-headers, & .font-headers a, & .font-regular-text, & .font-regular-text a': {
            color: colors.text.inverse
        },
        '& .key-service-icon-box': {
            background: colors.background.default
        },
        '& .key-service-icon': {
            filter: `${iconBlueFilter} !important`
        },
        '& .key-service-badge': {
            bgcolor: 'transparent',
            borderColor: colors.text.inverse,
            color: colors.text.inverse
        }
    }
} as const;

const iconBoxSx = {
    ...iconTileSx,
    mr: {xs: 0, sm: 2, md: 3},
    mb: {xs: 2, sm: 0},
    mt: {xs: 0.5, sm: 0},
    transition: `background-color ${transition.base}`
} as const;

const badgeSx = {
    display: 'inline-block',
    px: 1.5,
    py: 0.5,
    borderRadius: borderRadius.full,
    border: `1px solid ${colors.neutral.borderLight}`,
    bgcolor: colors.background.tag,
    fontSize: fontSize[14],
    color: colors.primary.deep,
    fontWeight: fontWeight.semibold,
    lineHeight: lineHeightRatio.snug,
    transition: `background-color ${transition.base}, border-color ${transition.base}, color ${transition.base}`
} as const;

const bodySx = {
    fontSize: fontSize[16],
    color: colors.text.primary
};

const KeyServices = ({heading, subheading, imageSrc, services}: KeyServicesProps) => {
    return (
        <Box>
            <MUIContainer>
                <Grid container spacing={{xs: 6, md: 8}}>
                    <Grid item xs={12} md={6}>
                        <Box position="sticky" top={150}>
                            <Box margin="0 auto">
                                <Typography variant="h1" component="h2" mb={3} fontSize={{xs: 35, sm: 48}} className="font-headers">
                                    {heading}
                                </Typography>

                                <Typography
                                    variant="subtitle1"
                                    component="p"
                                    className="font-regular-text remove-underline"
                                    sx={{fontSize: fontSize[16], color: colors.text.primary}}
                                >
                                    {parse(subheading)}
                                </Typography>
                            </Box>

                            {imageSrc && (
                                <Box sx={imageWrapperSx}>
                                    <DisplayImage src={imageSrc} alt="Key services deployment" fill style={{objectFit: 'cover'}} />
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={rightColumnSx}>
                            {services?.map((service, index) => {
                                const serviceHeading =
                                    service.title ??
                                    service.titleParts
                                        ?.map((part) => part.label)
                                        .join(' ')
                                        .trim() ??
                                    '';
                                const iconAlt = serviceHeading || 'service icon';
                                return (
                                    <AnimatedComponent key={index} variants={fadeInUp} delay={index * 0.1}>
                                        <Box sx={serviceCardSx}>
                                            {service.icon &&
                                                (React.isValidElement(service.icon) ? (
                                                    <Box sx={iconBoxSx} className="key-service-icon-box">
                                                        {service.icon}
                                                    </Box>
                                                ) : (
                                                    <Box sx={iconBoxSx} className="key-service-icon-box">
                                                        <DisplayImage
                                                            src={service.icon as string | StaticImageData}
                                                            alt={iconAlt}
                                                            width={ICON_TILE_ICON_SIZE}
                                                            height={ICON_TILE_ICON_SIZE}
                                                            className="key-service-icon"
                                                            style={iconTileImageStyle}
                                                        />
                                                    </Box>
                                                ))}

                                            <Box>
                                                <Typography
                                                    variant="h3"
                                                    component="h3"
                                                    sx={{fontSize: fontSize[20], pt: 0, pb: 1}}
                                                    className="font-headers"
                                                >
                                                    {service.titleParts ? (
                                                        service.titleParts?.map((part, i) =>
                                                            part.redirect ? (
                                                                <a
                                                                    key={i}
                                                                    href={part.redirect}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="remove-underline"
                                                                >
                                                                    {part.label}
                                                                </a>
                                                            ) : (
                                                                <span key={i}>{part.label}</span>
                                                            )
                                                        )
                                                    ) : service.redirect ? (
                                                        <a
                                                            href={service.redirect}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="card-redirection-heading"
                                                        >
                                                            {service.title}
                                                        </a>
                                                    ) : (
                                                        service.title
                                                    )}
                                                </Typography>

                                                <Typography variant="body1" className="font-regular-text remove-underline" sx={bodySx}>
                                                    {service.description ? parse(service.description) : ''}
                                                </Typography>

                                                {service.badges && service.badges.length > 0 && (
                                                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5}}>
                                                        {service.badges.map((badge, i) => (
                                                            <Box key={i} component="span" sx={badgeSx} className="key-service-badge">
                                                                {parse(badge)}
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>
                                    </AnimatedComponent>
                                );
                            })}
                        </Box>
                    </Grid>
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(KeyServices);
